import { apiFetch, getApiUrl } from "../auth/apiClient";

export const propertiesApi = {
  list: ({ type, q, bedrooms, furnishing, minPrice, maxPrice, cursor } = {}) => {
    const params = new URLSearchParams();
    if (type) params.set("type", type);
    if (q) params.set("q", q);
    if (bedrooms != null) params.set("bedrooms", String(bedrooms));
    if (furnishing) params.set("furnishing", furnishing);
    if (minPrice != null) params.set("min_price", String(minPrice));
    if (maxPrice != null) params.set("max_price", String(maxPrice));
    if (cursor) params.set("cursor", cursor);
    const qs = params.toString();
    return apiFetch(`/api/properties${qs ? `?${qs}` : ""}`, {
      method: "GET",
      authRequired: true,
    });
  },
  featured: () =>
    apiFetch("/api/properties/featured", {
      method: "GET",
      authRequired: true,
    }),
  myListings: ({ status } = {}) => {
    const qs = status ? `?status=${encodeURIComponent(status)}` : "";
    return apiFetch(`/api/properties/me/listings${qs}`, {
      method: "GET",
      authRequired: true,
    });
  },
  mySaves: () =>
    apiFetch("/api/properties/me/saves", {
      method: "GET",
      authRequired: true,
    }),
  myInquiries: ({ status } = {}) => {
    const qs = status ? `?status=${encodeURIComponent(status)}` : "";
    return apiFetch(`/api/properties/me/inquiries${qs}`, {
      method: "GET",
      authRequired: true,
    });
  },
  get: (id) =>
    apiFetch(`/api/properties/${id}`, { method: "GET", authRequired: true }),
  create: (body) =>
    apiFetch("/api/properties", {
      method: "POST",
      authRequired: true,
      body,
    }),
  patch: (id, body) =>
    apiFetch(`/api/properties/${id}`, {
      method: "PATCH",
      authRequired: true,
      body,
    }),
  publish: (id) =>
    apiFetch(`/api/properties/${id}/publish`, {
      method: "POST",
      authRequired: true,
      body: {},
    }),
  pause: (id) =>
    apiFetch(`/api/properties/${id}/pause`, {
      method: "POST",
      authRequired: true,
      body: {},
    }),
  resume: (id) =>
    apiFetch(`/api/properties/${id}/resume`, {
      method: "POST",
      authRequired: true,
      body: {},
    }),
  close: (id, outcome, reason) =>
    apiFetch(`/api/properties/${id}/close`, {
      method: "POST",
      authRequired: true,
      body: { outcome, reason: reason || null },
    }),
  remove: (id) =>
    apiFetch(`/api/properties/${id}`, {
      method: "DELETE",
      authRequired: true,
    }),
  uploadPhoto: (id, file, isPrimary = false) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("is_primary", isPrimary ? "true" : "false");
    return apiFetch(`/api/properties/${id}/photos`, {
      method: "POST",
      formData: fd,
      authRequired: true,
    });
  },
  patchPhoto: (id, photoId, body) =>
    apiFetch(`/api/properties/${id}/photos/${photoId}`, {
      method: "PATCH",
      authRequired: true,
      body,
    }),
  deletePhoto: (id, photoId) =>
    apiFetch(`/api/properties/${id}/photos/${photoId}`, {
      method: "DELETE",
      authRequired: true,
    }),
  save: (id) =>
    apiFetch(`/api/properties/${id}/save`, {
      method: "POST",
      authRequired: true,
      body: {},
    }),
  unsave: (id) =>
    apiFetch(`/api/properties/${id}/save`, {
      method: "DELETE",
      authRequired: true,
    }),
  submitInquiry: (id, body) =>
    apiFetch(`/api/properties/${id}/inquiries`, {
      method: "POST",
      authRequired: true,
      body,
    }),
  patchInquiry: (inquiryId, body) =>
    apiFetch(`/api/properties/inquiries/${inquiryId}`, {
      method: "PATCH",
      authRequired: true,
      body,
    }),
  externalOtpStart: (id, body) =>
    apiFetch(`/api/properties/${id}/external-otp/start`, {
      method: "POST",
      body,
    }),
  externalOtpVerify: (id, body) =>
    apiFetch(`/api/properties/${id}/external-otp/verify`, {
      method: "POST",
      body,
    }),
  externalViewers: (id) =>
    apiFetch(`/api/properties/${id}/external-viewers`, {
      method: "GET",
      authRequired: true,
    }),
  compare: (ids) => {
    const qs = Array.isArray(ids) ? ids.join(",") : ids;
    return apiFetch(`/api/properties/compare?ids=${encodeURIComponent(qs)}`, {
      method: "GET",
      authRequired: true,
    });
  },
  adminTakedown: (id, { reason, refund } = {}) =>
    apiFetch(`/api/properties/${id}/admin-takedown`, {
      method: "POST",
      authRequired: true,
      body: { reason: reason || null, refund: !!refund },
    }),
  adminRefundPayment: (paymentId) =>
    apiFetch(`/api/properties/payments/${paymentId}/refund`, {
      method: "POST",
      authRequired: true,
      body: {},
    }),
};

export function photoUrl(path) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${getApiUrl()}${path}`;
}

/**
 * Redirect the browser into the ICICI PgPay hosted payment page.
 *
 * ICICI is a hosted-page REDIRECT gateway, not a JS popup — and it
 * validates the HTTP `Referer` header, which only a real <form> submit
 * sends (an XHR/fetch would fail with a session-expired error). So we
 * build a hidden auto-submitting form from the backend's payload (whose
 * sensitive fields are already AES-encrypted server-side) and submit it.
 * This navigates away from the SPA; ICICI later redirects back to the
 * backend return URL, which 302s to /properties/me?payment=…
 *
 * @param {{action: string, fields: Record<string,string>}} payment
 */
export function startIciciPayment(payment) {
  if (!payment || !payment.action || !payment.fields) {
    throw new Error("Payment could not be started. Please try again.");
  }
  const form = document.createElement("form");
  form.method = "POST";
  form.action = payment.action;
  form.style.display = "none";
  Object.entries(payment.fields).forEach(([name, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name; // names may contain spaces (e.g. "Reference No")
    input.value = value == null ? "" : String(value);
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}
